import React, { useMemo, useCallback } from 'react';
import {
  Card, CardContent, Typography, Grid, FormControlLabel, Switch,
  Box, Button, IconButton, Tooltip, Chip
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import TimezoneSelector from '../../../../components/timezone';
import { BookingConfig, TimeRange } from '../../types';
import { NumberField } from '../Inputs/NumberField';
import { DayTimeRangesEditor } from '../Inputs/DayTimeRangesEditor';
import { SlotTimePicker } from '../Inputs/SlotTimePicker';
import { DAYS_OF_WEEK } from '../../constants';
import {
  calculateRangeSlotInfo,
  getPerDayWrapPartners,
  getWrapPartnersForRanges,
  isRangeSlotPerfectFit,
  validateTimeRanges,
} from '../../utils';

interface GeneralSettingsFormProps {
  formData: Partial<BookingConfig>;
  onChange: (field: string, value: any) => void;
  errors: Record<string, string | null>;
}

const LAST_MINUTE_OF_DAY = 23 * 60 + 59;

const buildNextRange = (lastRange: TimeRange | undefined, duration: number): TimeRange => {
  if (!lastRange?.end_time) {
    return { start_time: '09:00', end_time: '21:00' };
  }

  if (lastRange.end_time === '23:59') {
    return { start_time: '', end_time: '' };
  }

  const [h, m] = lastRange.end_time.split(':').map(Number);
  const endMins = h * 60 + m + duration;

  return {
    start_time: lastRange.end_time,
    end_time: endMins > LAST_MINUTE_OF_DAY
      ? ''
      : `${String(Math.floor(endMins / 60)).padStart(2, '0')}:${String(endMins % 60).padStart(2, '0')}`,
  };
};

export const GeneralSettingsForm: React.FC<GeneralSettingsFormProps> = ({
  formData,
  onChange,
  errors
}) => {
  const duration = formData.duration || 30;
  const globalRanges: TimeRange[] = formData.global_ranges || [];
  const isPerDaySchedule = !!formData.is_per_day_schedule;
  const globalWrapPartners = useMemo(() => getWrapPartnersForRanges(globalRanges), [globalRanges]);

  const slotInfo = useMemo(
    () => calculateRangeSlotInfo(globalRanges, duration, globalWrapPartners),
    [globalRanges, duration, globalWrapPartners]
  );
  const { totalMinutes: globalTotalMinutes, slots: globalSlots, remainder: globalRemainder } = slotInfo;
  const hasGlobalPerfectFit = globalRemainder === 0 && globalSlots > 0;

  const globalRangeErrors = useMemo(
    () => (isPerDaySchedule ? [] : validateTimeRanges(globalRanges as TimeRange[], duration)),
    [globalRanges, duration, isPerDaySchedule]
  );

  const getGlobalRangeError = useCallback(
    (index: number): string | null => {
      const error = globalRangeErrors.find((e) => e.index === index);
      return error ? error.message : null;
    },
    [globalRangeErrors]
  );

  const getPerDayRangeErrors = useCallback(
    (ranges: TimeRange[], day: number) => {
      if (!isPerDaySchedule) {
        return [];
      }

      const wrap = getPerDayWrapPartners(formData.days_info?.slots as any, day, formData.days);
      return validateTimeRanges(ranges, duration, wrap);
    },
    [isPerDaySchedule, duration, formData.days_info, formData.days]
  );

  const handleToggleDay = useCallback(
    (dayValue: number) => {
      const isActive = formData.days?.includes(dayValue);
      const key = String(dayValue);
      const currentSlots = (formData.days_info as any)?.slots || {};

      if (isActive) {
        const newDays = (formData.days || []).filter((d) => d !== dayValue);
        onChange('days', newDays);
      } else {
        const newDays = [...(formData.days || []), dayValue].sort((a, b) => a - b);
        const newSlots = { ...currentSlots, [key]: [{ start_time: '09:00', end_time: '21:00' }] };
        onChange('days', newDays);
        onChange('days_info', { slots: newSlots } as any);
      }
    },
    [formData.days, formData.days_info, onChange]
  );

  const handleRangesChange = useCallback(
    (dayValue: number, ranges: TimeRange[]) => {
      const key = String(dayValue);
      const currentSlots = (formData.days_info as any)?.slots || {};

      if (ranges.length === 0) {
        const newDays = (formData.days || []).filter((d) => d !== dayValue);
        onChange('days_info', { slots: currentSlots } as any);
        onChange('days', newDays);
        return;
      }
      const newSlots = { ...currentSlots, [key]: ranges };
      onChange('days_info', { slots: newSlots } as any);
    },
    [formData.days, formData.days_info, onChange]
  );

  const handleGlobalRangesChange = useCallback(
    (ranges: TimeRange[]) => {
      onChange('global_ranges', ranges as any);
    },
    [onChange]
  );

  const handleAddGlobalRange = useCallback(() => {
    const lastRange = globalRanges[globalRanges.length - 1];
    handleGlobalRangesChange([...globalRanges, buildNextRange(lastRange, duration)]);
  }, [duration, globalRanges, handleGlobalRangesChange]);

  const handleDeleteGlobalRange = useCallback(
    (index: number) => {
      handleGlobalRangesChange(globalRanges.filter((_, i) => i !== index));
    },
    [globalRanges, handleGlobalRangesChange]
  );

  const handleGlobalRangeChange = useCallback(
    (index: number, field: 'start_time' | 'end_time', value: string) => {
      const newRanges = globalRanges.map((r, i) => (i === index ? { ...r, [field]: value } : r));
      handleGlobalRangesChange(newRanges);
    },
    [globalRanges, handleGlobalRangesChange]
  );

  return (
    <Card sx={{ mb: 4, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
      <CardContent sx={{ p: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          General Settings
        </Typography>
        <Typography color="textSecondary" sx={{ mb: 3 }}>
          Configure the core booking preferences for this business.
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!formData.enabled}
                  onChange={(e) => onChange('enabled', e.target.checked)}
                  color="primary"
                />
              }
              label={formData.enabled ? 'Booking is Enabled' : 'Booking is Disabled'}
              sx={{ mb: 2, width: '100%' }}
            />

            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <TimezoneSelector
                  value={formData.timezone || 'IST'}
                  onChange={(val) => onChange('timezone', val)}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box data-booking-error-key="duration">
                  <NumberField
                    fullWidth
                    label="Slot Duration (Minutes)"
                    value={formData.duration ?? ''}
                    onChange={(val) => {
                      onChange('duration', val === '' ? null : parseInt(val));
                    }}
                    error={!!errors.duration || (formData.duration !== undefined && (!formData.duration || formData.duration < 15 || formData.duration % 15 !== 0))}
                    helperText={
                      formData.duration === undefined
                        ? 'Must be a multiple of 15 minutes'
                        : !formData.duration
                          ? 'Required — Enter a multiple of 15 minutes'
                          : (formData.duration % 15 !== 0 || formData.duration < 15)
                            ? `${formData.duration} min is not a valid multiple of 15`
                            : 'Must be a multiple of 15 minutes'
                    }
                    min={15}
                    step={15}
                    required={true}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} data-booking-error-key="days">
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                  Business Days
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3 }}>
                  {DAYS_OF_WEEK.map((day) => {
                    const isActive = formData.days?.includes(day.value);
                    return (
                      <Button
                        key={day.value}
                        variant={isActive ? 'contained' : 'outlined'}
                        size="small"
                        color={errors.days ? 'error' : 'primary'}
                        onClick={() => handleToggleDay(day.value)}
                        sx={{ minWidth: 60, borderRadius: 1.5 }}
                      >
                        {day.label}
                      </Button>
                    );
                  })}
                </Box>
                {errors.days && (
                  <Typography variant="caption" color="error" sx={{ mt: -2, mb: 3, display: 'block' }}>
                    {errors.days}
                  </Typography>
                )}
                <Box
                  data-booking-error-key="standard_hours"
                  sx={{
                    mb: 2,
                    opacity: isPerDaySchedule ? 0.55 : 1,
                    transition: 'opacity 0.2s ease',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Standard Hours
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {globalRanges.map((range, index) => {
                      const isFit = isRangeSlotPerfectFit(range, duration, globalWrapPartners, globalRanges);

                      return (
                        <Box
                          key={index}
                          sx={{
                            display: 'flex',
                            alignItems: { xs: 'stretch', sm: 'center' },
                            flexWrap: 'wrap',
                            gap: 1,
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: { xs: 'stretch', sm: 'center' },
                              flexWrap: 'wrap',
                              gap: 1,
                              flex: { xs: '1 1 100%', sm: '0 0 auto' },
                            }}
                          >
                            <SlotTimePicker
                              size="small"
                              value={range.start_time || ''}
                              onChange={(val) => handleGlobalRangeChange(index, 'start_time', val)}
                              duration={duration}
                              disabled={isPerDaySchedule}
                              sx={{ width: { xs: '100%', sm: 150 }, minWidth: 140 }}
                            />
                            <Typography color="textSecondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                              -
                            </Typography>
                            <SlotTimePicker
                              size="small"
                              value={range.end_time || ''}
                              onChange={(val) => handleGlobalRangeChange(index, 'end_time', val)}
                              duration={duration}
                              isEndTime
                              startTime={range.start_time}
                              showPerfectFit
                              showPerfectFitIndicator={false}
                              disabled={isPerDaySchedule}
                              allowMidnightWrap
                              rangeIndex={index}
                              ranges={globalRanges}
                              wrapPartners={globalWrapPartners}
                              sx={{ width: { xs: '100%', sm: 150 }, minWidth: 140 }}
                            />
                          </Box>

                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              flexWrap: 'nowrap',
                            }}
                          >
                            <Tooltip
                              title={
                                isFit
                                  ? 'Perfect fit - all time is bookable'
                                  : 'Adjust to eliminate remainder time'
                              }
                            >
                              <Chip
                                size="small"
                                color={isFit ? 'success' : 'warning'}
                                variant="outlined"
                                label={isFit ? 'Fit' : 'Adjust'}
                                sx={{
                                  height: 24,
                                  minWidth: 64,
                                  fontSize: '0.75rem',
                                  '& .MuiChip-label': {
                                    width: '100%',
                                    textAlign: 'center',
                                    px: 1,
                                  },
                                }}
                              />
                            </Tooltip>

                            <Tooltip title="Delete range">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteGlobalRange(index)}
                                  disabled={isPerDaySchedule}
                                  sx={{ color: 'error.main' }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>

                            {index === globalRanges.length - 1 && (
                              <Tooltip title="Add another range">
                                <span>
                                  <IconButton
                                    size="small"
                                    onClick={handleAddGlobalRange}
                                    disabled={isPerDaySchedule}
                                    sx={{
                                      color: 'primary.main',
                                      border: '1px dashed',
                                      borderColor: 'primary.main',
                                      borderRadius: 1,
                                      '&:hover': { borderStyle: 'solid' },
                                    }}
                                  >
                                    <AddIcon fontSize="small" />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            )}
                          </Box>

                          {getGlobalRangeError(index) && (
                            <Typography variant="caption" color="error" sx={{ width: '100%', mt: 0.5 }}>
                              {getGlobalRangeError(index)}
                            </Typography>
                          )}
                        </Box>
                      );
                    })}

                    {globalRanges.length === 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          No global ranges set
                        </Typography>
                        <Tooltip title="Add a global range">
                          <span>
                            <IconButton
                              size="small"
                              onClick={handleAddGlobalRange}
                              disabled={isPerDaySchedule}
                              sx={{
                                color: 'primary.main',
                                border: '1px dashed',
                                borderColor: 'primary.main',
                                borderRadius: 1,
                                '&:hover': { borderStyle: 'solid' },
                              }}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>

                  {globalTotalMinutes > 0 && (
                    <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="caption" color="text.primary" fontWeight={700}>
                        Total:
                      </Typography>
                      <Typography
                        variant="caption"
                        color={hasGlobalPerfectFit ? 'success.main' : 'warning.main'}
                        fontWeight={500}
                      >
                        {globalSlots} slots
                        {!hasGlobalPerfectFit && `, ${globalRemainder} min unschedulable`}
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: isPerDaySchedule ? 1 : 0 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isPerDaySchedule}
                        onChange={(e) => onChange('is_per_day_schedule', e.target.checked)}
                        color="primary"
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        Custom Business Hours
                      </Typography>
                    }
                    labelPlacement="start"
                    sx={{ m: 0 }}
                  />
                </Box>

                {isPerDaySchedule && (
                  <>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          Configure time slots for each day. Add multiple ranges for days with breaks.
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {DAYS_OF_WEEK.map((day) => {
                        const isActive = formData.days?.includes(day.value);
                        const key = String(day.value);
                        const overrides = formData.days_info?.slots || {};
                        const ranges = overrides[key] || [];
                        const rangeErrors = getPerDayRangeErrors(ranges as any, day.value);
                        const wrapPartners = getPerDayWrapPartners(overrides as any, day.value, formData.days);

                        return (
                          <DayTimeRangesEditor
                            key={day.value}
                            dayLabel={day.label}
                            dayValue={day.value}
                            isActive={!!isActive}
                            ranges={ranges as any}
                            duration={duration}
                            onToggle={() => handleToggleDay(day.value)}
                            onRangesChange={(newRanges) => handleRangesChange(day.value, newRanges)}
                            errors={errors}
                            rangeErrors={rangeErrors}
                            wrapPartners={wrapPartners}
                          />
                        );
                      })}
                    </Box>
                  </>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
